import type React from 'react';

import { useSingleAudioTrack } from 'contexts/SingleAudioTrackContext';
import type { Howl } from 'howler';

import { upCaseFirstLetter } from '@polyai/common/lib/stringHelper';
import type { AgentWizardOptionButtonProps } from '@polyai/ui/components/atoms/AgentWizardOptionButton';
import { AgentWizardOptionButton } from '@polyai/ui/components/atoms/AgentWizardOptionButton';
import { Stack } from '@polyai/ui/components/atoms/Stack';
import Text from '@polyai/ui/components/atoms/Text';

import type { AssistantVoiceOption } from 'api/types';
import { PlayAudioCell } from 'screens/AgentVoicesStream/components/VoiceSection/VoiceSection.styled';
import { getAccentLabel, getFlagOfLanguage } from 'utils/languageCode';

interface VoiceOptionButtonProps extends AgentWizardOptionButtonProps {
  voice: AssistantVoiceOption;
  isSelected: boolean;
  isLoading?: boolean;
  loadingVoiceId?: string;
  showFlag?: boolean;
  onPlay?: () => void;
}

const VoiceOptionButton: React.FC<VoiceOptionButtonProps> = ({
  voice,
  isSelected,
  isLoading,
  loadingVoiceId,
  showFlag = false,
  onPlay,
  ...rest
}) => {
  const { setPlayingTrack } = useSingleAudioTrack();

  const handlePlayAudio = (audio: Howl | undefined) => {
    setPlayingTrack(audio);
    onPlay?.();
  };

  return (
    <AgentWizardOptionButton
      {...rest}
      data-test-id={`agent-wizard-voice-option-${voice.name}`}
      isLoading={isLoading}
      isSelected={isSelected}
      shouldShowSelectedIcon={!loadingVoiceId}
    >
      <div className="flex justify-between items-center w-full mr-xs1">
        <Stack align="center" direction="row" spacing="xs1">
          {!!voice.voice_metadata.sample_voice?.length && (
            <PlayAudioCell
              url={voice.voice_metadata.sample_voice![0]}
              $noCircle
              onPlayAudio={handlePlayAudio}
            />
          )}
          <Text variant="bodySemibold">{upCaseFirstLetter(voice.name)}</Text>
        </Stack>
        <div className="flex gap-xxs1 items-center">
          {showFlag && (
            <Stack
              key={voice.id}
              background="backgroundAccent"
              padding="xxs1 xs2"
              radius="radiusXSmall"
            >
              <Text variant="bodySmallSemibold">
                {getFlagOfLanguage(voice.voice_metadata.accent)}{' '}
                {getAccentLabel(voice.voice_metadata.accent)}
              </Text>
            </Stack>
          )}
          <Stack align="center" direction="row" spacing="xs1">
            {voice.voice_metadata.tags?.slice(0, 3).map(tag => (
              <Stack
                key={tag}
                background="backgroundAccent"
                padding="xxs1 xs2"
                radius="radiusXSmall"
              >
                <Text variant="bodySmallSemibold">{tag}</Text>
              </Stack>
            ))}
          </Stack>
        </div>
      </div>
    </AgentWizardOptionButton>
  );
};

export default VoiceOptionButton;
